import React, { useState, useEffect } from "react";
import ProjectCard from "../components/ProjectCard";
import PublicHeader from "../components/PublicHeader";
import "bootstrap/dist/css/bootstrap.min.css";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import "../styles/ProjectPage.css";
import SiteMenu from "../components/SiteMenu";
import { useAuth } from "../context/AuthContext";
import { projectService } from "../services/ProjetService";
import { OffreService } from "../services/OffreService";
import { Project, Lot } from "../types/Project";

interface GraphData {
  mois: string;
  projetsEnCours: number;
  moyennePourcentage: number;
}

const MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const ProjectPage: React.FC = () => {
  const { api } = useAuth();

  const activities = [
    { label: "Appel d'offre", path: "/offre" },
    { label: "Projet", path: "/project" },
    { label: "Calendrier", path: "/calendar" },
    { label: "Événements", path: "/event" },
  ];

  const [graphData, setGraphData] = useState<GraphData[]>([]);
  const [dashboardSummary, setDashboardSummary] = useState<any>(null);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [dateDebut, setDateDebut] = useState<string>("");
  const [dateFin, setDateFin] = useState<string>("");

  useEffect(() => {
    const fetchDashboardAndProjects = async () => {
      try {
        const year = new Date().getFullYear();

        const summary = await OffreService.getDashboardSummary(api, year);
        setDashboardSummary(summary);

        const projects: Project[] = await projectService.getAllProjects(api);
        setAllProjects(projects);

        const filteredProjects = projects.filter(p => {
          if (!p.startDate || !p.endDate) return false;
          const start = new Date(p.startDate);
          const end = new Date(p.endDate);
          const filterStart = dateDebut ? new Date(dateDebut) : null;
          const filterEnd = dateFin ? new Date(dateFin) : null;

          if (filterStart && end < filterStart) return false;
          if (filterEnd && start > filterEnd) return false;
          return true;
        });

        const now = new Date();

        const allLots = filteredProjects.flatMap(p => p.lots ?? []);
        const pourcentageGlobal = allLots.length > 0
          ? Number((allLots.reduce((sum, l) => sum + l.percentage, 0) / allLots.length).toFixed(2))
          : 0;

        const projetsEnCours = filteredProjects.filter(p => {
          if (!p.startDate || !p.endDate) return false;
          const start = new Date(p.startDate);
          const end = new Date(p.endDate);
          return start <= now && end >= now;
        }).length;

        setDashboardSummary({
          ...summary,
          projetsPourcentageGlobal: pourcentageGlobal,
          projetsEnCours: projetsEnCours
        });

        const startMonth = dateDebut ? new Date(dateDebut).getMonth() : 0;
        const endMonth = dateFin ? new Date(dateFin).getMonth() : 11;

        const data: GraphData[] = MONTHS.map((monthName, index) => {
          if (index < startMonth || index > endMonth) return null;

          const month = index + 1;
          const projetsMois = filteredProjects.filter(p => {
            if (!p.startDate || !p.endDate) return false;
            const start = new Date(p.startDate);
            const end = new Date(p.endDate);
            return (start.getFullYear() < year || (start.getFullYear() === year && start.getMonth() + 1 <= month)) &&
                   (end.getFullYear() > year || (end.getFullYear() === year && end.getMonth() + 1 >= month));
          });

          const lots: Lot[] = projetsMois.flatMap(p => p.lots ?? []);
          const moyennePourcentage = lots.length > 0
            ? Number((lots.reduce((sum, l) => sum + l.percentage, 0) / lots.length).toFixed(2))
            : 0;

          return {
            mois: monthName,
            projetsEnCours: projetsMois.length,
            moyennePourcentage
          };
        }).filter(item => item !== null) as GraphData[];

        setGraphData(data);

      } catch (error) {
        console.error("Erreur lors de la récupération du dashboard ou des projets :", error);
      }
    };

    fetchDashboardAndProjects();
  }, [api, dateDebut, dateFin]);

  return (
    <div className="project-page-wrapper">
      <div className="map-page-header mb-3">
        <PublicHeader />
      </div>

      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-md-3 col-lg-2 mb-3 mb-md-0">
            <SiteMenu activities={activities} />
          </div>

          <main className="col-12 col-md-9 col-lg-10 project-page">

            {/* Titre et filtres sur la même ligne */}
            <div className="d-flex justify-content-between align-items-center mb-3 filters-container">
              <h2 className="m-0">Rapport d’activités du projet</h2>

              <div className="d-flex">
                <div className="filter-inline me-3">
                  <label>Date début</label>
                  <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} />
                </div>

                <div className="filter-inline">
                  <label>Date fin</label>
                  <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Statistiques en haut */}
            {dashboardSummary && (
              <div className="row mb-4">
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <ProjectCard label="En cours" value={dashboardSummary.projetsEnCours ?? 0} change={0} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <ProjectCard label="Pourcentage global" value={dashboardSummary.projetsPourcentageGlobal ?? 0} change={0} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <ProjectCard label="Total préstataires" value={dashboardSummary.totalPrestataire ?? 0} change={0} />
                </div>
                <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3">
                  <ProjectCard label="Prestataires déjà assignés" value={dashboardSummary.prestatairesDejaAccepte ?? 0} change={0} />
                </div>
              </div>
            )}

            {/* Graphique */}
            <div className="graph-card">
              <div className="graph-card-header">
                <div>
                  <h5 className="graph-title">Graphes des projets en cours et moyenne des lots</h5>
                </div>
              </div>

              <div className="graph-wrapper">
                <ResponsiveContainer width="95%" height={300}>
                  <AreaChart data={graphData}>
                    <defs>
                      <linearGradient id="colorProjets" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.6} />
                        <stop offset="95%" stopColor="#4CAF50" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="colorMoyenne" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1565C0" stopOpacity={0.6} />
                        <stop offset="95%" stopColor="#1565C0" stopOpacity={0.05} />
                      </linearGradient>
                    </defs>

                    <XAxis dataKey="mois" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ fontSize: 10 }} itemStyle={{ fontSize: 10 }} />
                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ fontSize: 12 }} />

                    <Area
                      type="monotone"
                      dataKey="projetsEnCours"
                      stroke="#2e7d32"
                      strokeWidth={2}
                      fill="url(#colorProjets)"
                      name="Projets en cours"
                    />
                    <Area
                      type="monotone"
                      dataKey="moyennePourcentage"
                      stroke="#1565C0"
                      strokeWidth={2}
                      fill="url(#colorMoyenne)"
                      name="Moyenne % lots"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
};

export default ProjectPage;
